import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} gallery
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'gallery', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #currentIndex = 0;
  #isTransitioning = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.gallery.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.gallery.removeEventListener('scroll', this.handleScroll);
  }

  async open(index, event) {
    event?.preventDefault();

    const { dialog } = this.refs;

    const realIndex = this.#getLoopedRealIndex(index);
    const actualIndex = this.#getActualIndexFromRealIndex(realIndex);
    const targetImage = this.refs.media[actualIndex];

    this.#currentIndex = realIndex;

    const open = () => {
      dialog.showModal();
      this.selectThumbnail(realIndex, { behavior: 'instant' });
    };

    const sourceImage = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(realIndex, { behavior: 'instant' });
  }

  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  handleScroll = debounce(async () => {
    const { media } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeActualIndex = media.indexOf(mostVisibleElement);

    if (activeActualIndex < 0) return;

    const realIndex = this.#getRealIndexFromActualIndex(activeActualIndex);

    this.#currentIndex = realIndex;
    this.#updateThumbnail(realIndex);
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(realIndex));

    if (!this.#hasLoopClones() || this.#isTransitioning) return;

    const total = this.#getRealMediaCount();

    if (activeActualIndex === 0) {
      requestAnimationFrame(() => this.#jumpToActualIndex(total));
    }

    if (activeActualIndex === total + 1) {
      requestAnimationFrame(() => this.#jumpToActualIndex(1));
    }
  }, 50);

  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeActualIndex = media.indexOf(mostVisibleElement);
    const activeRealIndex = this.#getRealIndexFromActualIndex(activeActualIndex);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeRealIndex]
      : mediaGallery?.media?.[activeRealIndex];

    if (!slide) return this.closeDialog();

    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next(event);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous(event);
    }
  }
handleDialogClick(event) {
  const target = event.target;

  if (!(target instanceof Element)) return;

  const clickedControl = target.closest(
    '.dialog-zoomed-gallery__arrow, .dialog-zoomed-gallery__close-button'
  );

  if (clickedControl) return;

  this.close();
}
  async next(event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.#isTransitioning) return;

    const total = this.#getRealMediaCount();
    if (total <= 1) return;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    const hasClones = this.#hasLoopClones();

    this.#isTransitioning = true;

    try {
      if (hasClones && this.#currentIndex === total - 1) {
        this.#selectActualIndex(total + 1, { behavior });

        if (behavior === 'smooth') {
          await this.#waitForScrollEnd(this.refs.gallery);
        }

        this.#jumpToActualIndex(1);
      } else {
        const nextRealIndex = this.#getLoopedRealIndex(this.#currentIndex + 1);
        const nextActualIndex = this.#getActualIndexFromRealIndex(nextRealIndex);

        this.#selectActualIndex(nextActualIndex, { behavior });

        if (behavior === 'smooth') {
          await this.#waitForScrollEnd(this.refs.gallery);
        }
      }
    } finally {
      this.#isTransitioning = false;
    }
  }

  async previous(event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.#isTransitioning) return;

    const total = this.#getRealMediaCount();
    if (total <= 1) return;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    const hasClones = this.#hasLoopClones();

    this.#isTransitioning = true;

    try {
      if (hasClones && this.#currentIndex === 0) {
        this.#selectActualIndex(0, { behavior });

        if (behavior === 'smooth') {
          await this.#waitForScrollEnd(this.refs.gallery);
        }

        this.#jumpToActualIndex(total);
      } else {
        const previousRealIndex = this.#getLoopedRealIndex(this.#currentIndex - 1);
        const previousActualIndex = this.#getActualIndexFromRealIndex(previousRealIndex);

        this.#selectActualIndex(previousActualIndex, { behavior });

        if (behavior === 'smooth') {
          await this.#waitForScrollEnd(this.refs.gallery);
        }
      }
    } finally {
      this.#isTransitioning = false;
    }
  }

  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  async handleThumbnailPointerEnter(index) {
    const actualIndex = this.#getActualIndexFromRealIndex(index);
    const targetImage = this.refs.media[actualIndex];

    if (!targetImage) return;

    this.loadHighResolutionImage(targetImage);
  }

  selectThumbnail(index, options = { behavior: 'smooth' }) {
    const realIndex = this.#getLoopedRealIndex(index);
    const actualIndex = this.#getActualIndexFromRealIndex(realIndex);

    this.#selectActualIndex(actualIndex, options);
  }

  #selectActualIndex(actualIndex, options = { behavior: 'smooth' }, shouldDispatch = true) {
    const { media } = this.refs;

    if (!media?.length) return;

    actualIndex = Math.max(0, Math.min(actualIndex, media.length - 1));

    const realIndex = this.#getRealIndexFromActualIndex(actualIndex);
    const targetImage = media[actualIndex];

    this.#currentIndex = realIndex;
    this.#updateThumbnail(realIndex);

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
        block: 'nearest',
        inline: 'center',
      });

      this.loadHighResolutionImage(targetImage);
    }

    if (shouldDispatch) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(realIndex));
    }
  }

  #jumpToActualIndex(actualIndex) {
    this.#selectActualIndex(actualIndex, { behavior: 'instant' }, false);
  }

  #updateThumbnail(realIndex) {
    const { thumbnails } = this.refs;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, index) => {
      button.setAttribute('aria-selected', `${index === realIndex}`);
    });

    const targetThumbnail = thumbnails.children[realIndex];

    if (targetThumbnail instanceof HTMLElement && targetThumbnail.offsetParent !== null) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }

  #getRealMediaCount() {
    const thumbnailCount = this.refs.thumbnails?.children?.length || 0;

    if (thumbnailCount > 0) return thumbnailCount;

    const realIndexes = this.refs.media
      .map((element) => Number(element.dataset.realIndex))
      .filter((index) => Number.isFinite(index));

    if (realIndexes.length > 0) return Math.max(...realIndexes) + 1;

    return this.refs.media.length;
  }

  #hasLoopClones() {
    const total = this.#getRealMediaCount();

    return total > 1 && this.refs.media.length === total + 2;
  }

  #getLoopedRealIndex(index) {
    const total = this.#getRealMediaCount();

    if (!total) return 0;

    return ((index % total) + total) % total;
  }

  #getActualIndexFromRealIndex(realIndex) {
    realIndex = this.#getLoopedRealIndex(realIndex);

    if (this.#hasLoopClones()) {
      return realIndex + 1;
    }

    return realIndex;
  }

  #getRealIndexFromActualIndex(actualIndex) {
    const total = this.#getRealMediaCount();

    if (!total) return 0;

    if (!this.#hasLoopClones()) {
      return this.#getLoopedRealIndex(actualIndex);
    }

    if (actualIndex === 0) return total - 1;
    if (actualIndex === total + 1) return 0;

    return this.#getLoopedRealIndex(actualIndex - 1);
  }

  #waitForScrollEnd(element) {
    return new Promise((resolve) => {
      let idleTimer;
      let maxTimer;
      let complete = false;

      const cleanup = () => {
        if (complete) return;

        complete = true;
        window.clearTimeout(idleTimer);
        window.clearTimeout(maxTimer);
        element.removeEventListener('scroll', onScroll);
        resolve();
      };

      const onScroll = () => {
        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(cleanup, 120);
      };

      element.addEventListener('scroll', onScroll, { passive: true });

      idleTimer = window.setTimeout(cleanup, 420);
      maxTimer = window.setTimeout(cleanup, 900);
    });
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}